import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import ImpersonationBanner from './ImpersonationBanner';

// Below this width the sidebar behaves as a slide-in drawer over the
// content (with a backdrop) instead of a column that pushes content over.
const MOBILE_BREAKPOINT = 767;

const Root = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: #f9fafb; /* gray-50 */
`;

const Backdrop = styled.div`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: ${(p) => (p.$show ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
    z-index: 45;
  }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  transition: margin-left 0.3s ease;
  margin-left: ${(p) => (p.$sidebarOpen ? '256px' : '80px')};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-left: 0;
  }
`;

const Content = styled.main`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f9fafb; /* gray-50 */
  padding: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
  }
`;

export default function DashboardLayout({ children, title, showStaffPresenceButton = false }) {
  const [sidebarOpen, setSidebarOpen] = useState(() => (
    typeof window === 'undefined' ? true : window.innerWidth > MOBILE_BREAKPOINT
  ));
  const wasMobileRef = useRef(typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);

  // Only react when a resize actually crosses the mobile/desktop boundary
  // (e.g. rotating a tablet, resizing a browser window) -- so it doesn't
  // fight the user's own open/close clicks within the same breakpoint.
  useEffect(() => {
    const handleResize = () => {
      const isMobileNow = window.innerWidth <= MOBILE_BREAKPOINT;
      if (isMobileNow !== wasMobileRef.current) {
        wasMobileRef.current = isMobileNow;
        setSidebarOpen(!isMobileNow);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On mobile the drawer is only ever meant to stay open long enough to
  // pick a destination -- close it once the user actually navigates.
  const closeOnMobileNavigate = () => {
    if (window.innerWidth <= MOBILE_BREAKPOINT) setSidebarOpen(false);
  };

  return (
    <Root>
      <Backdrop $show={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} onNavigate={closeOnMobileNavigate} />
      <Main $sidebarOpen={sidebarOpen}>
        <ImpersonationBanner />
        <TopBar
          title={title}
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          showStaffPresenceButton={showStaffPresenceButton}
        />
        <Content>{children}</Content>
      </Main>
    </Root>
  );
}
